import type {
  ComponentPropsWithoutRef,
  ElementType,
  MouseEventHandler,
  ReactElement,
  ReactNode,
} from 'react';
import type { To } from 'react-router';
import { merge } from 'lodash-es';

import type { DynamicIconName } from '../../../assets/DynamicIcon/DynamicIcon';
import type { BadgeGroupRecipe } from '../../../components/Badge/BadgeGroup';
import { Divider } from '../../../assets/Divider/Divider';
import { DynamicIcon } from '../../../assets/DynamicIcon/DynamicIcon';
import { selectors } from '../../../common/selectors';
import { transitions } from '../../../common/transitions';
import { Badge } from '../../../components/Badge/Badge';
import { BadgeGroup } from '../../../components/Badge/BadgeGroup';
import { Button } from '../../../controls/Button/Button';
import {
  type PreviewCardHandle,
  PreviewCard,
  PreviewCardContent,
  PreviewCardTrigger,
} from '../../../formatting/PreviewCard/PreviewCard';
import { colors, darkThemeSelector, shadows, styled } from '../../../stitches.config';
import { Body } from '../../../text/Body';
import { Small } from '../../../text/Small';
import { AlignStack } from '../../../utilities/AlignStack/AlignStack';
import { heightCSS } from '../../../utilities/shared/Height';
import { maxHeightCSS } from '../../../utilities/shared/MaxHeight';
import { maxWidthCSS } from '../../../utilities/shared/MaxWidth';
import { minHeightCSS } from '../../../utilities/shared/MinHeight';
import { minWidthCSS } from '../../../utilities/shared/MinWidth';
import { widthCSS } from '../../../utilities/shared/Width';
import { VStack } from '../../../utilities/Stack/VStack';
import {
  type HopObjectPreviewPayload,
  type HopPreview,
  type HopSharedProps,
  type HopsPreviewPayload,
  hopsVars,
} from './utils';

export type { HopObjectPreviewPayload, HopPreview } from './utils';

export interface HopObjectProps extends HopSharedProps {
  type: 'object';
  badges?: BadgeGroupRecipe;
  /** Icon name, or a custom element rendered in the icon slot (sized to fit the hop). */
  icon: DynamicIconName | ReactElement;
  label: ReactNode;
  preview?: HopPreview;
  /** When set with `preview`, only the trigger is rendered; `Hops` provides this for a shared popup. */
  previewHandle?: PreviewCardHandle<HopsPreviewPayload>;
  selected?: boolean;
  subline?: ReactNode;
  visibility?: 'visible' | 'hidden';
  // Routing
  as?: ElementType;
  to?: To;
  onClick?: MouseEventHandler<HTMLDivElement>;
}

const HopObjectLabel = styled(Body, {
  width: '100%',
  minWidth: 0,
  truncate: true,

  variants: {
    direction: {
      column: {
        color: colors.headingNeutralLight,

        [darkThemeSelector]: {
          color: colors.headingNeutralDark,
        },
      },
      row: {
        color: '$$hopObjectLabelColor',
        textAlign: 'center',
      },
    },
  },
});
HopObjectLabel.displayName = 'HopObjectLabel';

const HopObjectTarget = styled('div', {
  display: 'flex',
  minWidth: 0,
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: `$${hopsVars.padding.target.ends} $${hopsVars.padding.target.sides}`,
  borderRadius: '$10',
  transition: transitions.control,
  $$hopObjectLabelColor: colors.headingNeutralLight,

  [darkThemeSelector]: {
    $$hopObjectLabelColor: colors.headingNeutralDark,
  },

  variants: {
    isPressable: {
      true: {
        cursor: 'pointer',

        [selectors.hover]: {
          background: colors.bgNeutralLight,
          strokeAll: colors.strokeNeutralLight,

          [darkThemeSelector]: {
            background: colors.bgNeutralDark,
            strokeAll: colors.strokeNeutralDark,
          },
        },

        [selectors.focus]: {
          outline: 'none',
          background: colors.bgNeutralLight,
          boxShadow: shadows.focusRingLight,

          [darkThemeSelector]: {
            background: colors.bgNeutralDark,
            boxShadow: shadows.focusRingDark,
          },
        },
      },
      false: {},
    },
    selected: {
      true: {
        background: colors.bgBrandLight,
        strokeAll: colors.strokeBrandLight,
        $$hopObjectLabelColor: colors.headingBrandLight,

        [darkThemeSelector]: {
          background: colors.bgBrandDark,
          strokeAll: colors.strokeBrandDark,
          $$hopObjectLabelColor: colors.headingBrandDark,
        },

        [selectors.hover]: {
          background: colors.bgBrandLight,
          strokeAll: colors.strokeBrandLight,
          $$hopObjectLabelColor: colors.headingBrandLight,

          [darkThemeSelector]: {
            background: colors.bgBrandDark,
            strokeAll: colors.strokeBrandDark,
            $$hopObjectLabelColor: colors.headingBrandDark,
          },
        },

        [selectors.focus]: {
          background: colors.bgBrandLight,
          strokeAll: colors.strokeBrandLight,
          $$hopObjectLabelColor: colors.headingBrandLight,

          [darkThemeSelector]: {
            background: colors.bgBrandDark,
            strokeAll: colors.strokeBrandDark,
            $$hopObjectLabelColor: colors.headingBrandDark,
          },
        },
      },
      false: {},
    },
  },
});
HopObjectTarget.displayName = 'HopObjectTarget';

const HopObjectSubline = styled(Small);
HopObjectSubline.displayName = 'HopObjectSubline';

const HopObjectDetails = styled('div', {
  display: 'flex',

  variants: {
    direction: {
      row: {
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '$2',
      },
      column: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: '$8',
      },
    },
  },
});
HopObjectDetails.displayName = 'HopObjectDetails';

const HopObjectData = styled('div', {
  display: 'flex',

  variants: {
    direction: {
      row: {
        marginTop: `-$${hopsVars.padding.target.ends}`,
      },
      column: {
        flexDirection: 'column',
      },
    },
  },
});
HopObjectData.displayName = 'HopObjectData';

const HopObjectContent = styled('div', {
  display: 'flex',

  variants: {
    direction: {
      row: {
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      },
      column: {
        flexDirection: 'row',
        alignItems: 'center',
      },
    },
  },
});
HopObjectContent.displayName = 'HopObjectContent';

const HopObjectContainer = styled('div', {
  position: 'relative',
  zIndex: hopsVars.zIndex.object,
  display: 'flex',
  alignItems: 'center',

  variants: {
    direction: {
      row: {
        flexDirection: 'column',
      },
      column: {
        flexDirection: 'row',
      },
    },
    visibility: {
      visible: {},
      hidden: {
        opacity: 0,
        pointerEvents: 'none',
      },
    },
  },

  compoundVariants: [
    {
      direction: 'row',
      visibility: 'visible',
      css: {
        widthAll: 0,
        heightAll: 'min-content',
      },
    },
    {
      direction: 'row',
      visibility: 'hidden',
      css: {
        minWidth: 'min-content',
      },
    },
    {
      direction: 'column',
      visibility: 'visible',
      css: {
        heightAll: 0,
        widthAll: 'min-content',
      },
    },
    {
      direction: 'column',
      visibility: 'hidden',
      css: {
        minHeight: 'min-content',
      },
    },
  ],
});
HopObjectContainer.displayName = 'HopObjectContainer';

export function HopObjectSharedPreviewContent({ payload }: { payload: HopObjectPreviewPayload }) {
  const { content, icon, label, as, to, viewport, ...previewCardProps } = payload;
  const isPressable = Boolean(as && to);

  return (
    <PreviewCardContent
      gap={12}
      side="bottom"
      sideOffset={8}
      viewport={viewport ?? true}
      {...previewCardProps}
    >
      <VStack spacing={12}>
        <AlignStack
          gap={8}
          preset="body"
          start={typeof icon === 'string' ? <DynamicIcon icon={icon} size={28} /> : icon}
          end={
            isPressable ? (
              <Button
                as={as}
                to={to}
                arrangement="hidden-label"
                ghost
                icon="arrow-right"
                size="small"
                variant="secondary"
              >
                View
              </Button>
            ) : undefined
          }
        >
          {label}
        </AlignStack>
        <Divider direction="row" />
        {content}
      </VStack>
    </PreviewCardContent>
  );
}

export function HopObject({
  badges,
  direction,
  icon,
  id,
  label,
  preview,
  previewHandle,
  selected,
  size = 'small',
  subline,
  visibility,
  width,
  maxWidth,
  minWidth,
  height,
  maxHeight,
  minHeight,
  // Routing
  as,
  to,
  onClick,
}: HopObjectProps) {
  const isPressable = Boolean((as && to) || onClick);

  const hasBadges = badges && badges.items.length > 0;
  const hasData = subline || hasBadges;
  const sizingCSS = merge(
    widthCSS(width),
    maxWidthCSS(maxWidth),
    minWidthCSS(minWidth),
    heightCSS(height),
    maxHeightCSS(maxHeight),
    minHeightCSS(minHeight),
  );
  const targetCSS = sizingCSS;
  const containerCSS = visibility === 'hidden' ? sizingCSS : {};

  const labelElement =
    typeof label === 'string' ? (
      <HopObjectLabel direction={direction} weight="bold">
        {label}
      </HopObjectLabel>
    ) : (
      label
    );

  const hopObjectProps: ComponentPropsWithoutRef<typeof HopObjectContainer> = {
    id,
    direction,
    visibility,
    css: containerCSS,
  };

  const hopObject = (
    <HopObjectContent direction={direction}>
      <HopObjectTarget
        css={targetCSS}
        as={as}
        to={to}
        onClick={onClick}
        tabIndex={isPressable ? 0 : undefined}
        isPressable={isPressable}
        selected={selected}
      >
        {typeof icon === 'string' ? (
          <DynamicIcon
            icon={icon}
            priority={size === 'small' ? ['framed'] : undefined}
            size={direction === 'row' ? hopsVars.icon.row[size] : hopsVars.icon.column[size]}
          />
        ) : (
          icon
        )}
        {direction === 'row' && labelElement}
      </HopObjectTarget>
      {hasData && (
        <HopObjectData direction={direction}>
          {direction === 'column' && labelElement}
          <HopObjectDetails direction={direction}>
            {subline && <HopObjectSubline whitespace="no-wrap">{subline}</HopObjectSubline>}
            {hasBadges && (
              <BadgeGroup ends="pill" relation="joined" size="small" {...badges}>
                {badges.items.map((badge) => (
                  <Badge key={badge.id} {...badge} />
                ))}
              </BadgeGroup>
            )}
          </HopObjectDetails>
        </HopObjectData>
      )}
    </HopObjectContent>
  );

  if (preview && previewHandle) {
    return (
      <PreviewCardTrigger
        closeDelay={300}
        delay={50}
        handle={previewHandle}
        payload={{
          ...preview,
          kind: 'object',
          icon,
          label: labelElement,
          as,
          to,
        }}
        render={<HopObjectContainer {...hopObjectProps}>{hopObject}</HopObjectContainer>}
      />
    );
  }

  if (preview) {
    return (
      <PreviewCard>
        <PreviewCardTrigger
          render={<HopObjectContainer {...hopObjectProps}>{hopObject}</HopObjectContainer>}
        />
        <HopObjectSharedPreviewContent
          payload={{
            ...preview,
            kind: 'object',
            icon,
            label: labelElement,
            as,
            to,
          }}
        />
      </PreviewCard>
    );
  }

  return <HopObjectContainer {...hopObjectProps}>{hopObject}</HopObjectContainer>;
}
